<template>
  <div class="bg-[#00554A]">
    <van-nav-bar left-text="" :title="$t('convidarCentro.title')" left-arrow class="custom-nav-bar"
       :border="false">
      <template #left>
        <Button type="back" @click="onClickLeft"></Button>
      </template>
    </van-nav-bar>
    <div class="tab-container bg-[#001C18] py-[2px] px-[3px] rounded-full relative z-[10]">
      <div class="tabstyles" v-trans="{
        type: 'heartbeat',
        scale: 1.05,
        duration: 100
      }" v-for="(item, index) in filteredTabs" :key="item.value" :class="{ active: activeTab === index }"
        @click="handleTabClick(index, $event)" ref="tabRefs">
        {{ item.label }}
      </div>
    </div>
    <div class="tab-content p-[10px] overflow-x-hidden">
      <component :is="currentComponent" @switchTab="handleSwitchTab" />
    </div>

  </div>
</template>
<script setup lang="ts">
import { Button } from '@/components/Button'
import { ref, shallowRef, nextTick, defineAsyncComponent, watch, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import useUIStore from '@/stores/modules/ui'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const router = useRouter()
const uiStore = useUIStore()
const onClickLeft = () => router.back()

// 所有可能的标签页配置
const allTabs = computed(() => [
  { label: t('convidarCentro.inviteLink'), value: 0, type: 1 },
  { label: t('convidarCentro.agentNetworkTitle'), value: 1, type: 2 },
  { label: t('convidarCentro.agentBonusRateTitle'), value: 2, type: 3 },
  { label: t('convidarCentro.performance'), value: 3, type: 4 },
  { label: t('convidarCentro.registerSubordinates'), value: 4, type: 5 },
])

// 所有可能的组件
const allTabComponents = [
  defineAsyncComponent(() => import('./components/InviteLink.vue')),
  defineAsyncComponent(() => import('./components/AgentNetwork.vue')),
  defineAsyncComponent(() => import('./components/AgentBonusRate.vue')),
  defineAsyncComponent(() => import('./components/Performance.vue')),
  defineAsyncComponent(() => import('./components/RegisterSub.vue')),
]

// 根据 promoterType 过滤标签页
const filteredTabs = computed(() => {
  const config = uiStore.promoterConfig
  console.log('promoterConfig:', config)
  console.log('promoterType:', config?.promoterType)

  if (!config?.promoterType) {
    console.log('没有promoterType配置，显示所有标签页')
    return allTabs.value // 如果没有配置，显示所有标签页
  }

  const filtered = allTabs.value.filter(tab => {
    const configItem = config.promoterType.find((item: any) => item.type === tab.type)
    console.log(`标签 ${tab.label} (type: ${tab.type}):`, configItem, 'showSwitch:', configItem?.showSwitch)
    return configItem && configItem.showSwitch
  }).sort((a, b) => {
    const configA = config.promoterType.find((item: any) => item.type === a.type)
    const configB = config.promoterType.find((item: any) => item.type === b.type)
    return (configB?.sort || 0) - (configA?.sort || 0)
  })

  console.log('过滤后的标签页:', filtered)
  return filtered
})

// 根据过滤后的标签页生成对应的组件
const filteredTabComponents = computed(() => {
  return filteredTabs.value.map(tab => {
    const originalIndex = allTabs.value.findIndex(t => t.type === tab.type)
    return allTabComponents[originalIndex]
  })
})

// 从 store 获取保存的 activeTab，如果没有则默认为 0
const activeTab = ref(uiStore.getConvidarCentroActiveTab())
const tabRefs = ref([])

const currentComponent = shallowRef(null)

// 监听 activeTab 变化，确保组件正确更新
watch(activeTab, (newValue) => {
  if (filteredTabComponents.value[newValue]) {
    console.log('activeTab changed to:', newValue, 'label:', filteredTabs.value[newValue]?.label)
    currentComponent.value = filteredTabComponents.value[newValue]
  }
})

// 监听过滤后的标签页变化
watch(filteredTabs, (newTabs) => {
  console.log('Filtered tabs updated:', newTabs)
  // 如果当前选中的标签页不在过滤后的列表中，重置为第一个
  if (newTabs.length > 0 && (activeTab.value >= newTabs.length || !newTabs[activeTab.value])) {
    activeTab.value = 0
  }
  // 更新当前组件
  if (filteredTabComponents.value[activeTab.value]) {
    currentComponent.value = filteredTabComponents.value[activeTab.value]
  }
}, { immediate: true })

function handleTabClick(index: number, event: MouseEvent) {
  if (index >= filteredTabs.value.length) return

  console.log('Tab clicked:', index, 'label:', filteredTabs.value[index]?.label)
  activeTab.value = index
  currentComponent.value = filteredTabComponents.value[index]
  // 保存到 store
  uiStore.setConvidarCentroActiveTab(index)
  nextTick(() => {
    scrollToCenter(index)
  })
}

function handleSwitchTab(tabIdentifier: number | string) {
  let targetIndex: number

  if (typeof tabIdentifier === 'string') {
    // 如果是字符串，根据标签类型查找索引
    const tabTypeMap: Record<string, number> = {
      'invite': 1,      // 邀请链接
      'network': 2,     // 代理网络
      'bonus': 3,       // 代理奖金率
      'commission': 4,  // 业绩
      'register': 5     // 注册下级
    }

    const targetType = tabTypeMap[tabIdentifier]
    if (targetType) {
      // 在过滤后的标签页中找到对应type的索引
      targetIndex = filteredTabs.value.findIndex(tab => tab.type === targetType)
    } else {
      console.warn('Unknown tab type:', tabIdentifier)
      return
    }
  } else {
    // 如果是数字，直接使用索引
    targetIndex = tabIdentifier
  }

  if (targetIndex >= 0 && targetIndex < filteredTabs.value.length) {
    console.log('Switch tab to:', targetIndex, 'label:', filteredTabs.value[targetIndex]?.label)
    // 调用 handleTabClick 来确保所有逻辑都执行
    handleTabClick(targetIndex, {} as MouseEvent)
  } else {
    console.warn('Invalid tab index:', targetIndex)
  }
}

function scrollToCenter(index: number) {
  const tabEls = tabRefs.value as HTMLElement[]
  const container = document.querySelector('.tab-container') as HTMLElement
  if (!tabEls || !container) return
  const tab = tabEls[index]
  if (!tab) return
  // 让当前tab居中
  const containerWidth = container.offsetWidth
  const tabLeft = tab.offsetLeft
  const tabWidth = tab.offsetWidth
  const scrollLeft = tabLeft - (containerWidth / 2) + (tabWidth / 2)
  container.scrollTo({ left: scrollLeft, behavior: 'smooth' })
}

const route = useRoute()

onMounted(async () => {
  // 清除缓存并重新获取推广配置
  uiStore.clearPromoterConfig()
  await uiStore.getPromoterConfig()

  console.log('Initial activeTab:', activeTab.value, 'label:', filteredTabs.value[activeTab.value]?.label)
  console.log('Store activeTab:', uiStore.getConvidarCentroActiveTab())

  const tabIndex = route.query.tab
  if (tabIndex !== undefined) {
    const index = parseInt(tabIndex as string)
    if (index >= 0 && index < filteredTabs.value.length) {
      // 调用 handleTabClick 来确保所有逻辑都执行
      handleTabClick(index, {} as MouseEvent)
    }
  } else {
    // 如果没有 URL 参数，也要调用 handleTabClick 来初始化
    if (filteredTabs.value.length > 0) {
      handleTabClick(activeTab.value, {} as MouseEvent)
    }
  }
})

// 页面卸载前保存当前状态
onBeforeUnmount(() => {
  uiStore.setConvidarCentroActiveTab(activeTab.value)
})
</script>
<style lang="scss" scoped>
.custom-nav-bar {
  height: 56px;
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.3), 0px -2px 22px 0px rgba(53, 189, 128, 0.61);

  &>* {
    height: 100%;
  }
}

.tab-container {
  display: flex;
  overflow-x: auto;
  margin: 10px;
  scrollbar-width: none;
  /* 隐藏滚动条 */
  gap: 10px;
  /* 添加间距 */
}

.tab-container::-webkit-scrollbar {
  display: none;
}

.tabstyles {
  height: 29px;
  text-align: center;
  // background: #18553A;
  // box-shadow: 1px 1px 2px 0px rgba(21, 76, 55, 0.32);
  // border-radius: 6px;
  font-size: 11px;
  min-width: 104px;
  color: #fff;
  padding: 8px 8px;
  margin-right: 0;
  /* 移除右边距，使用gap */
  flex-shrink: 0;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  /* 为调试标记定位 */
}

.tabstyles.active {
  background: getPath('home/btn__sel_this.png') no-repeat !important;
  background-size: 100% 100% !important;
  color: #fff !important;
  // box-shadow: 1px 1px 2px 0px rgba(19, 199, 105, 0.32) !important;
  transform: scale(1.02);
  /* 轻微放大效果 */
}

/* 悬停效果 */
// .tabstyles:hover:not(.active) {
//   background: #1a5f42;
//   transform: scale(1.01);
// }

/* 调试信息样式 */
.debug-info {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  margin: 0 10px;
}
</style>

<route lang="json5">
    {
      name: 'ConvidarCentro',
      meta: {
        title: 'ConvidarCentro',
        i18n: 'menus.convidarCentro'
      },
    }
</route>
